import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceArea, ResponsiveContainer,
} from 'recharts';
import api from '../services/api';
import type { NetworkRequestReport, NetworkRequestTimeseries, NetworkRequestRow } from '../types';
import UsageUserFilter from '../components/UsageUserFilter';
import {
  brushToDateRange,
  bucketsSoFar,
  formatPeriodHeader,
  isCurrentOrFuturePeriod,
  shiftPeriod,
  type Period,
} from './usagePeriods';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-sm">
      <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">{label}</div>
      {payload.map((entry: { name: string; value: number; color: string }, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-600 dark:text-gray-300">{entry.name}:</span>
          <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

// Network downloader strategies (DownloaderType). '' = all.
const DOWNLOADER_TYPES = ['Http', 'Crawl4ai', 'Serper', 'BrowserPlugin', 'BrowserPluginV2'] as const;

export default function NetworkRequestReport() {
  const [period, setPeriod] = useState<Period>('month');
  const [periodDate, setPeriodDate] = useState<string | undefined>(undefined);
  const [downloaderType, setDownloaderType] = useState<string>('');
  // '' = my own usage. Admin-only; see UsageUserFilter.
  const [userScope, setUserScope] = useState<string>('');
  const [report, setReport] = useState<NetworkRequestReport | null>(null);
  const [timeseries, setTimeseries] = useState<NetworkRequestTimeseries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [brushRange, setBrushRange] = useState<[number, number] | null>(null);
  const [dragStart, setDragStart] = useState<string | null>(null);
  const [dragEnd, setDragEnd] = useState<string | null>(null);
  const [expandedEngine, setExpandedEngine] = useState<string | null>(null);
  const [rowDetails, setRowDetails] = useState<NetworkRequestRow[] | null>(null);
  const [rowDetailsLoading, setRowDetailsLoading] = useState(false);
  const [rowDetailsTotal, setRowDetailsTotal] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [reportData, tsData] = await Promise.all([
        api.getNetworkRequestReport(period, periodDate, downloaderType || undefined, userScope || undefined),
        api.getNetworkRequestTimeseries(period, periodDate, downloaderType || undefined, userScope || undefined),
      ]);
      setReport(reportData);
      setTimeseries(tsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load network request report');
    } finally {
      setLoading(false);
    }
  }, [period, periodDate, downloaderType, userScope]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePeriodChange = (p: Period) => {
    setPeriod(p);
    setPeriodDate(undefined);
    setBrushRange(null);
    setExpandedEngine(null);
    setRowDetails(null);
  };

  const handlePrev = () => {
    if (!report?.period_start) return;
    setPeriodDate(shiftPeriod(report.period_start, period, -1));
    setBrushRange(null);
    setExpandedEngine(null);
    setRowDetails(null);
  };

  const handleNext = () => {
    if (!report?.period_start) return;
    setPeriodDate(shiftPeriod(report.period_start, period, 1));
    setBrushRange(null);
    setExpandedEngine(null);
    setRowDetails(null);
  };

  const nextDisabled = report?.period_start
    ? isCurrentOrFuturePeriod(report.period_start, period)
    : true;

  const clearSelection = () => {
    setBrushRange(null);
    setExpandedEngine(null);
    setRowDetails(null);
  };

  const finalizeDrag = () => {
    if (!dragStart || !dragEnd || !timeseries) {
      setDragStart(null);
      setDragEnd(null);
      return;
    }
    const allLabels = timeseries.buckets.map((b) => b.label);
    let si = allLabels.indexOf(dragStart);
    let ei = allLabels.indexOf(dragEnd);
    if (si === -1 || ei === -1) { setDragStart(null); setDragEnd(null); return; }
    if (si > ei) [si, ei] = [ei, si];
    if (si === 0 && ei === allLabels.length - 1) {
      setBrushRange(null);
    } else {
      setBrushRange([si, ei]);
    }
    setExpandedEngine(null);
    setRowDetails(null);
    setDragStart(null);
    setDragEnd(null);
  };

  const handleRowClick = async (searchEngine: string | null) => {
    const key = searchEngine ?? '__none__';
    if (expandedEngine === key) {
      setExpandedEngine(null);
      setRowDetails(null);
      return;
    }
    if (!report?.period_start || !timeseries) return;
    setExpandedEngine(key);
    setRowDetails(null);
    setRowDetailsLoading(true);
    try {
      const { start, end } = brushToDateRange(brushRange, period, report.period_start, timeseries.buckets.length);
      const resp = await api.getNetworkRequestRows(start, end, searchEngine ?? undefined, undefined, downloaderType || undefined, userScope || undefined);
      setRowDetails(resp.rows);
      setRowDetailsTotal(resp.total_count);
    } catch {
      setRowDetails([]);
    } finally {
      setRowDetailsLoading(false);
    }
  };

  const allBuckets = React.useMemo(
    () => bucketsSoFar(timeseries?.buckets ?? [], period, report?.period_start),
    [timeseries, report?.period_start, period],
  );

  const chartData = brushRange
    ? allBuckets.slice(brushRange[0], brushRange[1] + 1)
    : allBuckets;

  const hasTimeseries = timeseries && timeseries.buckets.length > 0;

  const shiftRange = (direction: -1 | 1) => {
    const maxIdx = allBuckets.length - 1;
    if (maxIdx < 1) return;
    const curStart = brushRange ? brushRange[0] : 0;
    const curEnd = brushRange ? brushRange[1] : maxIdx;
    const step = brushRange
      ? Math.max(1, Math.floor((curEnd - curStart + 1) / 2))
      : Math.max(1, Math.floor(allBuckets.length / 4));
    let newStart = curStart + direction * step;
    let newEnd = curEnd + direction * step;
    if (!brushRange) {
      if (direction === -1) { newEnd = maxIdx; newStart = Math.max(0, maxIdx - step + 1); }
      else { newStart = 0; newEnd = Math.min(maxIdx, step - 1); }
    }
    if (newStart < 0) { newEnd -= newStart; newStart = 0; }
    if (newEnd > maxIdx) { newStart -= (newEnd - maxIdx); newEnd = maxIdx; }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(maxIdx, newEnd);
    if (newStart === 0 && newEnd === maxIdx) { clearSelection(); }
    else { setBrushRange([newStart, newEnd]); setExpandedEngine(null); setRowDetails(null); }
  };

  const pct = (n: number, total: number) => total > 0 ? `${Math.round((n / total) * 100)}%` : '-';

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Network Usage</h2>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden text-sm">
          {(['hour', 'day', 'month', 'year'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => handlePeriodChange(p)}
              className={`px-3 py-1.5 capitalize ${period === p ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            >
              {p}
            </button>
          ))}
        </div>

        {/* Downloader-type filter */}
        <select
          value={downloaderType}
          onChange={(e) => {
            setDownloaderType(e.target.value);
            setBrushRange(null);
            setExpandedEngine(null);
            setRowDetails(null);
          }}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          title="Filter by network downloader strategy"
        >
          <option value="">All downloaders</option>
          {DOWNLOADER_TYPES.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        <UsageUserFilter
          value={userScope}
          onChange={(next) => {
            setUserScope(next);
            // The open drill-down was fetched for the OLD scope; leaving it up
            // would show one person's requests under another person's name.
            clearSelection();
          }}
        />

        {report?.period_start && (
          <div className="flex items-center gap-1">
            <button onClick={handlePrev} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300" title="Previous period">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[140px] text-center">
              {formatPeriodHeader(report.period_start, period)}
            </span>
            <button
              onClick={handleNext}
              disabled={nextDisabled}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next period"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Summary cards */}
      {report && report.total_requests > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Requests</div>
            <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{report.total_requests.toLocaleString()}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Successes</div>
            <div className="text-xl font-semibold text-green-700 dark:text-green-300 mt-0.5">
              {report.total_successes.toLocaleString()}
              <span className="text-sm font-normal text-gray-400 dark:text-gray-500 ml-1">{pct(report.total_successes, report.total_requests)}</span>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Captchas</div>
            <div className="text-xl font-semibold text-amber-600 dark:text-amber-400 mt-0.5">
              {report.total_captchas.toLocaleString()}
              <span className="text-sm font-normal text-gray-400 dark:text-gray-500 ml-1">{pct(report.total_captchas, report.total_requests)}</span>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Errors</div>
            <div className="text-xl font-semibold text-red-600 dark:text-red-400 mt-0.5">
              {report.total_errors.toLocaleString()}
              <span className="text-sm font-normal text-gray-400 dark:text-gray-500 ml-1">{pct(report.total_errors, report.total_requests)}</span>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {/* Chart */}
      {!loading && hasTimeseries && (
        <div className="bg-white dark:bg-gray-800 border rounded-lg p-4 mb-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Requests {period === 'hour' ? 'per Minute' : period === 'day' ? 'per Hour' : period === 'month' ? 'per Day' : 'per Month'}
          </h3>
          <div className="w-full" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                onMouseDown={(e) => { if (e?.activeLabel != null) setDragStart(String(e.activeLabel)); }}
                onMouseMove={(e) => { if (dragStart && e?.activeLabel != null) setDragEnd(String(e.activeLabel)); }}
                onMouseUp={finalizeDrag}
                onMouseLeave={() => { if (dragStart) { setDragStart(null); setDragEnd(null); } }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  interval={chartData.length <= 12 ? 0 : chartData.length <= 30 ? 1 : Math.floor(chartData.length / 15)}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 11 }} width={40} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="request_count" name="Requests" stroke="#6366f1" strokeWidth={2} dot={chartData.length <= 30} />
                <Line type="monotone" dataKey="success_count" name="Successes" stroke="#22c55e" strokeWidth={2} dot={chartData.length <= 30} />
                <Line type="monotone" dataKey="captcha_count" name="Captchas" stroke="#f59e0b" strokeWidth={2} dot={chartData.length <= 30} />
                <Line type="monotone" dataKey="error_count" name="Errors" stroke="#ef4444" strokeWidth={2} dot={chartData.length <= 30} />
                {dragStart && dragEnd && (
                  <ReferenceArea x1={dragStart} x2={dragEnd} strokeOpacity={0.3} fill="#6366f1" fillOpacity={0.15} />
                )}
                <Legend verticalAlign="bottom" height={28} iconType="line" iconSize={14} wrapperStyle={{ fontSize: 12 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Range controls */}
          {allBuckets.length > 0 && (
            <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex-wrap">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium mr-1">Range:</span>
              <button
                onClick={() => shiftRange(-1)}
                disabled={brushRange != null && brushRange[0] === 0}
                className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <select
                value={brushRange ? brushRange[0] : 0}
                onChange={(e) => {
                  const ns = Number(e.target.value);
                  const end = Math.max(ns, brushRange ? brushRange[1] : allBuckets.length - 1);
                  if (ns === 0 && end === allBuckets.length - 1) clearSelection();
                  else { setBrushRange([ns, end]); setExpandedEngine(null); setRowDetails(null); }
                }}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {allBuckets.map((b, i) => <option key={i} value={i}>{b.label}</option>)}
              </select>
              <span className="text-xs text-gray-400 dark:text-gray-500">to</span>
              <select
                value={brushRange ? brushRange[1] : allBuckets.length - 1}
                onChange={(e) => {
                  const ne = Number(e.target.value);
                  const start = Math.min(brushRange ? brushRange[0] : 0, ne);
                  if (start === 0 && ne === allBuckets.length - 1) clearSelection();
                  else { setBrushRange([start, ne]); setExpandedEngine(null); setRowDetails(null); }
                }}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {allBuckets.map((b, i) => <option key={i} value={i}>{b.label}</option>)}
              </select>
              <button
                onClick={() => shiftRange(1)}
                disabled={brushRange != null && brushRange[1] >= allBuckets.length - 1}
                className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {brushRange && (
                <button onClick={clearSelection} className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium ml-2 px-2 py-1 rounded border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/30">
                  Reset zoom
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>
      ) : !report || report.rows.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
          No network requests recorded yet
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Search Engine</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Requests</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Successes</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Captchas</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Errors</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Avg Response</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {report.rows.map((row, idx) => {
                const key = row.search_engine ?? '__none__';
                const isExpanded = expandedEngine === key;
                return (
                  <React.Fragment key={idx}>
                    <tr
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer select-none"
                      onClick={() => handleRowClick(row.search_engine)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <svg
                            className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="text-gray-900 dark:text-gray-100">{row.search_engine ?? '(unknown)'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-gray-600 dark:text-gray-300">{row.request_count}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-green-700 dark:text-green-300">
                        {row.success_count}
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">{pct(row.success_count, row.request_count)}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-amber-600 dark:text-amber-400">
                        {row.captcha_count}
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">{pct(row.captcha_count, row.request_count)}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-red-600 dark:text-red-400">
                        {row.error_count}
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">{pct(row.error_count, row.request_count)}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-gray-600 dark:text-gray-300">
                        {row.avg_response_time != null ? `${row.avg_response_time.toFixed(2)}s` : '-'}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} className="bg-gray-50 dark:bg-gray-800 px-4 py-3">
                          {rowDetailsLoading ? (
                            <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">Loading requests...</div>
                          ) : !rowDetails || rowDetails.length === 0 ? (
                            <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">No requests found in this range</div>
                          ) : (
                            <>
                              {rowDetailsTotal > rowDetails.length && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                  Showing {rowDetails.length} of {rowDetailsTotal} requests
                                </div>
                              )}
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                                    <th className="text-left py-1.5 pr-3 font-medium">Time</th>
                                    <th className="text-left py-1.5 pr-3 font-medium">URL</th>
                                    <th className="text-right py-1.5 pr-3 font-medium">Status</th>
                                    <th className="text-right py-1.5 pr-3 font-medium">Response</th>
                                    <th className="text-right py-1.5 font-medium">Captcha</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                  {rowDetails.map((req) => (
                                    <tr key={req.id} className="hover:bg-gray-100 dark:hover:bg-gray-700">
                                      <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-300 font-mono whitespace-nowrap text-xs">
                                        {new Date(req.created_at).toLocaleTimeString('en-US', { hour12: false })}
                                      </td>
                                      <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-300 max-w-xs truncate text-xs font-mono" title={req.url}>
                                        {req.url}
                                      </td>
                                      <td className="py-1.5 pr-3 text-right font-mono text-xs">
                                        <span className={req.status_code != null && req.status_code < 400 ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-400'}>
                                          {req.status_code ?? '-'}
                                        </span>
                                      </td>
                                      <td className="py-1.5 pr-3 text-right font-mono text-xs text-gray-600 dark:text-gray-300">
                                        {req.response_time != null ? `${req.response_time.toFixed(2)}s` : '-'}
                                      </td>
                                      <td className="py-1.5 text-right text-xs">
                                        {req.captcha_encountered
                                          ? <span className="text-amber-600 dark:text-amber-400 font-medium">Yes</span>
                                          : <span className="text-gray-400 dark:text-gray-500">No</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
